// Repository picker of the organization's active GitHub connections (spec §3.1, D36): pick a connection, filter, choose
// one repository (private badge, default branch). Shared by "New project → Import a GitHub repository" and "Connect
// repository". Lists come from GET /v1/integrations and GET /v1/integrations/:id/github/repositories; with no active
// GitHub connection it says so and links to Integrations.
import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { GithubRepository, Integration } from "../../api/types-wave3.ts";
import { useI18n } from "../../i18n/index.tsx";
import { listGithubRepositories, listIntegrations } from "../../api/endpoints-wave3.ts";
import { useAsync, type AsyncState } from "../../lib/useAsync.ts";
import { SelectField, TextField } from "../../components/Field.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { LockIcon } from "../../components/icons.tsx";

export interface GithubPicker {
  integrations: AsyncState<Awaited<ReturnType<typeof listIntegrations>> | null>;
  connections: Integration[];
  connection: Integration | null;
  setIntegrationId: (id: string) => void;
  repositories: AsyncState<Awaited<ReturnType<typeof listGithubRepositories>> | null>;
  filter: string;
  setFilter: (v: string) => void;
  shown: GithubRepository[];
  all: GithubRepository[];
  /** The chosen repository, only while the filter shows it. */
  repository: GithubRepository | null;
  setSelected: (fullName: string) => void;
}

/** State and data of the picker; `active` gates the requests (closed dialog = no calls). */
export function useGithubPicker(active: boolean, initialSelected = ""): GithubPicker {
  const [integrationId, setIntegrationId] = useState("");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(initialSelected);

  useEffect(() => {
    if (!active) return;
    setIntegrationId("");
    setFilter("");
    setSelected(initialSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const integrations = useAsync((s) => (active ? listIntegrations(s) : Promise.resolve(null)), [active]);
  const connections = (integrations.data?.items ?? []).filter((i) => (i.kind === "github_app" || i.kind === "github_token") && i.state === "active");
  const connection = connections.find((c) => c.id === integrationId) ?? connections[0] ?? null;
  const repositories = useAsync((s) => (active && connection ? listGithubRepositories(connection.id, s) : Promise.resolve(null)), [active, connection?.id]);
  const all = repositories.data?.repositories ?? [];
  const needle = filter.trim().toLowerCase();
  const shown = needle ? all.filter((r) => r.fullName.toLowerCase().includes(needle)) : all;
  // Only a visible choice counts: a selection the filter hides keeps the submit disabled until it shows again.
  const repository = shown.find((r) => r.fullName.toLowerCase() === selected.toLowerCase()) ?? null;
  return { integrations, connections, connection, setIntegrationId, repositories, filter, setFilter, shown, all, repository, setSelected };
}

export function GithubRepositoryPicker({ picker, onLeave }: { picker: GithubPicker; onLeave?: () => void }) {
  const { t } = useI18n();
  const { integrations, connections, connection, repositories, shown, all, repository } = picker;
  if (integrations.loading) return <LoadingBlock label={t("common.loading")} lines={1} />;
  if (integrations.error) return <ErrorBanner error={integrations.error} onRetry={integrations.reload} />;
  if (!connection) {
    return (
      <p data-testid="github-not-connected">
        {t("createProject.noGithub")}{" "}
        <Link to="/integrations?tab=connections" onClick={onLeave}>
          {t("createProject.openIntegrations")}
        </Link>
      </p>
    );
  }
  return (
    <>
      <SelectField label={t("githubOnboarding.connection")} value={connection.id} onChange={(e) => picker.setIntegrationId(e.target.value)}>
        {connections.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </SelectField>
      <fieldset className="stack">
        <legend className="field-label">{t("githubOnboarding.repository")}</legend>
        <TextField
          label={t("githubOnboarding.filter")}
          type="search"
          value={picker.filter}
          onChange={(e) => picker.setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault(); // filtering never submits the form
          }}
          spellCheck={false}
        />
        {repositories.loading ? (
          <LoadingBlock label={t("common.loading")} lines={2} />
        ) : repositories.error ? (
          <ActionError error={repositories.error} />
        ) : shown.length === 0 ? (
          <p className="muted">{all.length === 0 ? t("githubOnboarding.noRepositories") : t("githubOnboarding.noMatch")}</p>
        ) : (
          <ul className="plain-list github-repo-list">
            {shown.map((r) => (
              <li key={r.fullName} data-testid="github-repository">
                <label className="choice">
                  <input type="radio" name="github-repository" value={r.fullName} checked={repository?.fullName === r.fullName} onChange={() => picker.setSelected(r.fullName)} />
                  <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                    <span className="row row--wrap">
                      <span className="mono user-content">{r.fullName}</span>
                      {r.private ? (
                        <span className="pill pill--neutral">
                          <LockIcon size={12} />
                          <span>{t("githubOnboarding.private")}</span>
                        </span>
                      ) : null}
                    </span>
                    {r.defaultBranch ? <span className="subtle">{t("githubOnboarding.defaultBranch", { branch: r.defaultBranch })}</span> : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </fieldset>
    </>
  );
}
